/**
 * React Query hooks for Membership Requests
 *
 * Handles listing and reviewing pending membership requests for club leaders.
 * Issue #1239 - Request Access feature
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clubsApi,
  type PendingMembershipRequestsResponse,
  type ReviewMembershipRequestPayload,
  type ReviewMembershipRequestResponse,
} from '../lib/api/clubs';
import { useOroMutation, OroOperation } from './useOroMutation';

/**
 * Fetch pending membership requests for a club (leaders only)
 *
 * Query Key: ['membership-requests', clubId, 'pending']
 * Returns: { requests: PendingMembershipRequest[] }
 */
export function usePendingMembershipRequests(clubId: string) {
  return useQuery<PendingMembershipRequestsResponse>({
    queryKey: ['membership-requests', clubId, 'pending'],
    queryFn: () => clubsApi.getPendingMembershipRequests(clubId),
    enabled: !!clubId,
    staleTime: 1000 * 30, // 30 seconds - requests may change frequently
  });
}

/**
 * Review (approve/deny) a membership request
 *
 * On success: Invalidates pending requests query to refresh the list
 */
export function useReviewMembershipRequest(clubId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<
    ReviewMembershipRequestResponse,
    { requestId: string; action: ReviewMembershipRequestPayload['action'] }
  >({
    mutationFn: ({ requestId, action }) =>
      clubsApi.reviewMembershipRequest(clubId, requestId, { action }),
    onSuccess: (_, { action }) => {
      // Refresh pending requests list
      queryClient.invalidateQueries({ queryKey: ['membership-requests', clubId, 'pending'] });
      // Refresh club members list if approved
      if (action === 'approve') {
        queryClient.invalidateQueries({ queryKey: ['clubMembers'] });
        queryClient.invalidateQueries({ queryKey: ['club'] });
      }
    },
    successToast: (data) =>
      data.request.status === 'approved' ? 'Member approved' : 'Request denied',
    oro: {
      op: OroOperation.ReviewMembershipRequest,
      retryPolicy: 'never',
      debugLabel: 'Membership request review',
    },
  });
}
